import type { LocalDict } from "../../core/i18n";

// OAuth consent page (an outside MCP/A2A client asking to reach agents).
export const STR: LocalDict = {
  en: {
    "consent.title": "Allow {client} to talk to your agents?",
    "consent.subtitle":
      "An outside application signed in with OAuth wants to send messages to agents on this deployment. Only an administrator can allow it.",
    "consent.client": "Application",
    "consent.clientId": "Client id",
    "consent.redirect": "Will be sent back to",
    "consent.redirectHint":
      "After you decide, your browser is redirected to this address, which the application registered when it signed up. If you do not recognise it, decline.",
    "consent.resource": "Requested endpoint",
    "consent.agents": "Agents this application may reach",
    "consent.agentsHint":
      "Tick the agents the application may send messages to. Each call runs as an outside, non-owner caller under the agent's own approval rules; anything the agent would normally ask a human about waits in Approvals. Untick everything to grant nothing.",
    "consent.all": "Every agent published for external access, now and in the future",
    "consent.allHint":
      "The application asked for all agents. Granting this means agents you publish later are reachable without a new sign-in. Prefer naming agents unless you trust the application fully.",
    "consent.requestedNote": "The application asked for: {agents}.",
    "consent.noneExposed":
      "No agent is published for external access yet. Turn on MCP or A2A on an agent (agent → External access) and ask the application to sign in again.",
    "consent.expires": "This request expires in {minutes} min.",
    "consent.approve": "Allow",
    "consent.deny": "Decline",
    "consent.working": "Working…",
    "consent.invalid.title": "This sign-in request is unknown or has expired",
    "consent.invalid.body":
      "Consent requests live for ten minutes and can be used once. Go back to the application and start the sign-in again.",
    "consent.forbidden.title": "Only an administrator can approve this",
    "consent.forbidden.body":
      "You are signed in, but this account is not an administrator of the deployment. Ask an admin to open this link.",
    "consent.err.generic": "Could not record your decision. Try again.",
    "consent.err.pick": "Pick at least one agent, or decline.",
    "consent.redirecting": "Redirecting you back to {client}…",
    "consent.back": "Back to the dashboard",
  },
  zh: {
    "consent.title": "允许 {client} 与您的智能体对话？",
    "consent.subtitle": "一个通过 OAuth 登录的外部应用希望向此部署中的智能体发送消息。只有管理员可以允许。",
    "consent.client": "应用",
    "consent.clientId": "客户端 ID",
    "consent.redirect": "决定后返回到",
    "consent.redirectHint": "您做出决定后，浏览器会被重定向到该地址（应用注册时声明的地址）。如果您不认识它，请拒绝。",
    "consent.resource": "请求的端点",
    "consent.agents": "此应用可以访问的智能体",
    "consent.agentsHint": "勾选应用可以向其发送消息的智能体。每次调用都作为外部非所有者调用者运行，并遵循智能体自身的审批规则；智能体通常需要询问人类的操作会在“审批”中等待。全部取消勾选则不授予任何权限。",
    "consent.all": "所有已发布用于外部访问的智能体（现在和将来）",
    "consent.allHint": "应用请求了所有智能体。授予后，以后发布的智能体无需重新登录即可访问。除非完全信任该应用，否则建议指定智能体。",
    "consent.requestedNote": "应用请求了：{agents}。",
    "consent.noneExposed": "尚无智能体发布用于外部访问。请在智能体 → 外部访问中开启 MCP 或 A2A，然后让应用重新登录。",
    "consent.expires": "此请求将在 {minutes} 分钟后过期。",
    "consent.approve": "允许",
    "consent.deny": "拒绝",
    "consent.working": "处理中…",
    "consent.invalid.title": "此登录请求未知或已过期",
    "consent.invalid.body": "同意请求有效期为十分钟且只能使用一次。请返回应用重新开始登录。",
    "consent.forbidden.title": "只有管理员可以批准",
    "consent.forbidden.body": "您已登录，但此账户不是该部署的管理员。请让管理员打开此链接。",
    "consent.err.generic": "无法记录您的决定，请重试。",
    "consent.err.pick": "至少选择一个智能体，或拒绝。",
    "consent.redirecting": "正在将您重定向回 {client}…",
    "consent.back": "返回控制台",
  },
  es: {
    "consent.title": "¿Permitir que {client} hable con tus agentes?",
    "consent.subtitle": "Una aplicación externa que inició sesión con OAuth quiere enviar mensajes a agentes de este despliegue. Solo un administrador puede permitirlo.",
    "consent.client": "Aplicación",
    "consent.clientId": "Id de cliente",
    "consent.redirect": "Se devolverá a",
    "consent.redirectHint": "Tras decidir, el navegador te redirige a esta dirección, la que la aplicación registró al darse de alta. Si no la reconoces, rechaza.",
    "consent.resource": "Punto de acceso solicitado",
    "consent.agents": "Agentes a los que puede llegar esta aplicación",
    "consent.agentsHint": "Marca los agentes a los que la aplicación puede enviar mensajes. Cada llamada se ejecuta como un llamante externo sin propiedad, bajo las reglas de aprobación del propio agente; lo que el agente normalmente consultaría a una persona espera en Aprobaciones. Desmarca todo para no conceder nada.",
    "consent.all": "Todos los agentes publicados para acceso externo, ahora y en el futuro",
    "consent.allHint": "La aplicación pidió todos los agentes. Concederlo implica que los agentes que publiques más adelante serán accesibles sin un nuevo inicio de sesión. Es preferible nombrar agentes salvo que confíes plenamente en la aplicación.",
    "consent.requestedNote": "La aplicación pidió: {agents}.",
    "consent.noneExposed": "Ningún agente está publicado para acceso externo todavía. Activa MCP o A2A en un agente (agente → Acceso externo) y pide a la aplicación que inicie sesión de nuevo.",
    "consent.expires": "Esta solicitud caduca en {minutes} min.",
    "consent.approve": "Permitir",
    "consent.deny": "Rechazar",
    "consent.working": "Procesando…",
    "consent.invalid.title": "Esta solicitud de inicio de sesión es desconocida o ha caducado",
    "consent.invalid.body": "Las solicitudes de consentimiento duran diez minutos y se usan una sola vez. Vuelve a la aplicación e inicia sesión de nuevo.",
    "consent.forbidden.title": "Solo un administrador puede aprobar esto",
    "consent.forbidden.body": "Has iniciado sesión, pero esta cuenta no es administradora del despliegue. Pide a un administrador que abra este enlace.",
    "consent.err.generic": "No se pudo registrar tu decisión. Inténtalo de nuevo.",
    "consent.err.pick": "Elige al menos un agente, o rechaza.",
    "consent.redirecting": "Redirigiéndote a {client}…",
    "consent.back": "Volver al panel",
  },
};
